import { useState, useEffect } from 'react';
import { socieApi } from '../services/api';
import UploadSOCIEModal from './UploadSOCIEModal';
import GenerateSOCIEModal from './GenerateSOCIEModal';
import '../styles/Panel.css';

function SOCIEPanel({ entity, onComplete }) {
  const [outputs, setOutputs] = useState([]);
  const [inputStatus, setInputStatus] = useState(null);
  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  useEffect(() => {
    if (entity) {
      fetchData();
    }
  }, [entity]);

  const fetchData = async () => {
    try {
      // Fetch outputs
      const outputsData = await socieApi.listOutputs(entity.name);
      setOutputs(outputsData.outputs || []);

      // Fetch input data status
      const statusData = await socieApi.getStatus(entity.name);
      setInputStatus(statusData);
    } catch (err) {
      console.error('Failed to fetch SOCIE data:', err);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setError(null);
      const blob = await socieApi.downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'SOCIE_Template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download template: ' + err.message);
    }
  };

  const handleDownloadOutput = async (output) => {
    try {
      setError(null);
      const blob = await socieApi.downloadOutput(
        entity.name,
        output.period,
        output.timestamp
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `socie_${entity.name}_${output.period}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download output: ' + err.message);
    }
  };

  const handleUploadComplete = () => {
    setShowUploadModal(false);
    fetchData();
    if (onComplete) onComplete();
  };

  const handleGenerateComplete = () => {
    setShowGenerateModal(false);
    fetchData();
    if (onComplete) onComplete();
  };

  return (
    <div className="panel socie-panel">
      <h2>SOCIE (Statement of Changes in Equity)</h2>
      <p className="panel-description">
        Generate Schedule III & IndAS compliant Statement of Changes in Equity with direct equity input data
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Step 1: Download Template */}
      <div className="section">
        <h3>Step 1: Download & Fill Input Template</h3>
        <div className="button-group">
          <button
            className="btn btn-secondary"
            onClick={handleDownloadTemplate}
          >
            Download SOCIE Template
          </button>
        </div>
        <small className="form-text">
          Download the template, fill in equity share capital data (shares, share price) and other equity data (retained earnings, profit, share based payments from ESOP)
        </small>
      </div>

      {/* Step 2: Upload Input Data */}
      <div className="section">
        <h3>Step 2: Upload Filled Template</h3>
        <div className="button-group">
          <button
            className="btn btn-primary"
            onClick={() => setShowUploadModal(true)}
          >
            Upload Input Data
          </button>
        </div>
        {inputStatus?.has_data && (
          <div className="info-box">
            <p><strong>Latest Input Data Upload:</strong></p>
            <p>Uploaded: {new Date(inputStatus.last_upload).toLocaleString()}</p>
            {inputStatus.equity_data && (
              <div>
                <p><strong>Equity Share Capital:</strong></p>
                <ul>
                  <li>Opening Shares: {inputStatus.equity_data['Opening Number of Shares']?.toLocaleString() || 'N/A'}</li>
                  <li>Shares Issued: {inputStatus.equity_data['Shares Issued During Year']?.toLocaleString() || 'N/A'}</li>
                  <li>Share Price: ₹{inputStatus.equity_data['Share Price']?.toLocaleString() || 'N/A'}</li>
                </ul>
              </div>
            )}
            {inputStatus.other_equity_data && (
              <div>
                <p><strong>Other Equity:</strong></p>
                <ul>
                  <li>Opening Retained Earnings: ₹{inputStatus.other_equity_data['Opening Balance - Distributable Retained Earnings']?.toLocaleString() || 'N/A'}</li>
                  <li>Profit: ₹{inputStatus.other_equity_data['Profit of the Year']?.toLocaleString() || 'N/A'}</li>
                  <li>Share Based Payments (ESOP): ₹{inputStatus.other_equity_data['Share Based Payments (From ESOP)']?.toLocaleString() || 'N/A'}</li>
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Step 3: Generate SOCIE */}
      <div className="section">
        <h3>Step 3: Generate SOCIE Report</h3>
        <div className="button-group">
          <button
            className="btn btn-primary"
            onClick={() => setShowGenerateModal(true)}
            disabled={!inputStatus?.has_data}
          >
            Generate SOCIE
          </button>
        </div>
        <small className="form-text">
          {inputStatus?.has_data
            ? 'Generate SOCIE statement using the uploaded input data. All calculations will be formula-based in Excel.'
            : 'Please upload input data first before generating SOCIE.'}
        </small>
      </div>

      {/* Generated Reports */}
      {outputs.length > 0 && (
        <div className="section">
          <h3>Generated SOCIE Reports</h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Opening Shares</th>
                <th>Shares Issued</th>
                <th>Closing Shares</th>
                <th>Share Price</th>
                <th>Total Opening Equity</th>
                <th>Total Closing Equity</th>
                <th>Generated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {outputs.map((output, index) => (
                <tr key={index}>
                  <td>{output.period}</td>
                  <td>
                    {output.metadata?.opening_shares
                      ? parseFloat(output.metadata.opening_shares).toLocaleString('en-IN')
                      : '-'}
                  </td>
                  <td>
                    {output.metadata?.shares_issued
                      ? parseFloat(output.metadata.shares_issued).toLocaleString('en-IN')
                      : '-'}
                  </td>
                  <td>
                    {output.metadata?.closing_shares
                      ? parseFloat(output.metadata.closing_shares).toLocaleString('en-IN')
                      : '-'}
                  </td>
                  <td>
                    {output.metadata?.share_price
                      ? `₹${parseFloat(output.metadata.share_price).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}`
                      : '-'}
                  </td>
                  <td>
                    {output.metadata?.total_opening_equity
                      ? `₹${parseFloat(output.metadata.total_opening_equity).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}`
                      : '-'}
                  </td>
                  <td>
                    {output.metadata?.total_closing_equity
                      ? `₹${parseFloat(output.metadata.total_closing_equity).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}`
                      : '-'}
                  </td>
                  <td>
                    {new Date(output.last_modified).toLocaleString()}
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleDownloadOutput(output)}
                    >
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Feature Highlights */}
      <div className="section info-section">
        <h3>Features</h3>
        <ul>
          <li><strong>Direct Input Approach:</strong> Simple input template for equity share capital and other equity data</li>
          <li><strong>Schedule III Compliant:</strong> Generates reports following Indian Companies Act format</li>
          <li><strong>IndAS Standards:</strong> Complies with Indian Accounting Standards</li>
          <li><strong>100% Formula-Based:</strong> All calculations in Excel formulas referencing Input Data sheet - no hardcoded values</li>
          <li><strong>ESOP Integration:</strong> Built-in field for share based payment expense from ESOP calculations</li>
          <li><strong>Complete Transparency:</strong> All formulas visible for audit trail - click any cell to see calculation</li>
          <li><strong>Easy to Modify:</strong> Update Input Data sheet values and SOCIE Statement auto-recalculates</li>
          <li><strong>Fast Processing:</strong> No AI or Trial Balance required - instant generation</li>
        </ul>
      </div>

      {/* Modals */}
      {showUploadModal && (
        <UploadSOCIEModal
          entity={entity}
          onClose={() => setShowUploadModal(false)}
          onComplete={handleUploadComplete}
        />
      )}

      {showGenerateModal && (
        <GenerateSOCIEModal
          entity={entity}
          onClose={() => setShowGenerateModal(false)}
          onComplete={handleGenerateComplete}
        />
      )}
    </div>
  );
}

export default SOCIEPanel;
